import { useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import type { WorkoutAssignment, WorkoutSession } from '@/types';

interface WeeklyCalendarProps {
  assignments: WorkoutAssignment[];
  sessions: WorkoutSession[];
  templateNames: Map<string, string>;
  onStartWorkout?: (assignment: WorkoutAssignment) => void;
}

interface DayData {
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  isPast: boolean;
  assignments: WorkoutAssignment[];
  completedSessions: WorkoutSession[];
}

export function WeeklyCalendar({
  assignments,
  sessions,
  templateNames,
  onStartWorkout,
}: WeeklyCalendarProps) {
  const getAssignmentWorkoutName = (assignment: WorkoutAssignment) =>
    assignment.workoutTemplateName ||
    templateNames.get(assignment.workoutTemplateId) ||
    'Workout';

  const weekDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get start of week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    const days: DayData[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);

      const dateStr = date.toDateString();
      const todayStr = today.toDateString();

      // Find assignments for this day
      const dayAssignments = assignments.filter((a) => {
        const assignmentDate = (a.scheduledFor as Timestamp).toDate();
        return assignmentDate.toDateString() === dateStr;
      });

      // Find completed sessions for this day
      const dayCompletedSessions = sessions.filter((s) => {
        if (s.status !== 'completed' || !s.completedAt) return false;
        const completedDate = (s.completedAt as Timestamp).toDate();
        return completedDate.toDateString() === dateStr;
      });

      days.push({
        date,
        dayName: dayNames[i],
        dayNumber: date.getDate(),
        isToday: dateStr === todayStr,
        isPast: date < today,
        assignments: dayAssignments,
        completedSessions: dayCompletedSessions,
      });
    }

    return days;
  }, [assignments, sessions]);

  const getStatusForDay = (day: DayData): 'completed' | 'scheduled' | 'missed' | 'empty' => {
    // Check if there's a completed session (from sessions or assignment status)
    const hasCompletedSession = day.completedSessions.length > 0 ||
      day.assignments.some(a => a.status === 'completed');

    if (hasCompletedSession) return 'completed';

    // Check for pending/in-progress workouts
    const hasPending = day.assignments.some(
      (a) => a.status === 'pending' || a.status === 'reminded' || a.status === 'confirmed' || a.status === 'in_progress'
    );
    if (hasPending) return 'scheduled';

    // Check for missed (past day with assignment that wasn't completed)
    const hasMissed = day.isPast && day.assignments.some(
      (a) => a.status === 'missed' || a.status === 'skipped' || a.status === 'abandoned'
    );
    if (hasMissed) return 'missed';

    return 'empty';
  };

  const getWorkoutName = (day: DayData): string | null => {
    if (day.assignments.length > 0) {
      return getAssignmentWorkoutName(day.assignments[0]);
    }
    if (day.completedSessions.length > 0) {
      return day.completedSessions[0].workoutSnapshot?.name || 'Workout';
    }
    return null;
  };

  const getTodayAssignment = (day: DayData): WorkoutAssignment | null => {
    if (!day.isToday) return null;
    return day.assignments.find(
      (a) => a.status === 'pending' || a.status === 'reminded' || a.status === 'confirmed'
    ) || null;
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-white/10 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          This Week
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            Done
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-primary-500"></span>
            Scheduled
          </span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {weekDays.map((day) => {
          const status = getStatusForDay(day);
          const workoutName = getWorkoutName(day);
          const todayAssignment = getTodayAssignment(day);

          return (
            <div
              key={day.date.toISOString()}
              className={`
                relative flex flex-col items-center p-1.5 sm:p-2 rounded-lg transition-all
                ${day.isToday
                  ? 'ring-2 ring-primary-500 ring-offset-1 dark:ring-offset-slate-900'
                  : ''
                }
                ${status === 'completed'
                  ? 'bg-emerald-50 dark:bg-emerald-500/10'
                  : status === 'scheduled'
                    ? 'bg-primary-50 dark:bg-primary-500/10'
                    : status === 'missed'
                      ? 'bg-red-50 dark:bg-red-500/10'
                      : 'bg-gray-50 dark:bg-white/5'
                }
                ${day.isPast && status === 'empty' ? 'opacity-50' : ''}
              `}
            >
              {/* Day Name */}
              <span className={`
                text-[10px] sm:text-xs font-medium uppercase tracking-wide
                ${day.isToday
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 dark:text-slate-400'
                }
              `}>
                {day.dayName}
              </span>

              {/* Day Number */}
              <span className={`
                text-sm sm:text-lg font-semibold mt-0.5
                ${day.isToday
                  ? 'text-primary-600 dark:text-primary-400'
                  : status === 'completed'
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : status === 'scheduled'
                      ? 'text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-slate-300'
                }
              `}>
                {day.dayNumber}
              </span>

              {/* Status Icon */}
              <div className="h-5 flex items-center justify-center mt-1">
                {status === 'completed' && (
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {status === 'scheduled' && (
                  <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-primary-500 animate-pulse"></span>
                )}
                {status === 'missed' && (
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>

              {/* Workout Name (hidden on mobile for space) */}
              {workoutName && (
                <span className="hidden sm:block text-[9px] text-center text-gray-500 dark:text-slate-400 mt-1 truncate max-w-full px-0.5">
                  {workoutName}
                </span>
              )}

              {/* Quick Start Button for Today */}
              {todayAssignment && onStartWorkout && (
                <button
                  onClick={() => onStartWorkout(todayAssignment)}
                  className="
                    absolute -bottom-1 left-1/2 -translate-x-1/2
                    px-2 py-0.5 text-[9px] font-medium
                    bg-primary-500 hover:bg-primary-600
                    text-white rounded-full shadow-sm
                    transition-all hover:scale-105
                    hidden sm:block
                  "
                >
                  Start
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: Today's Workout Preview */}
      {weekDays.some(d => d.isToday && getStatusForDay(d) === 'scheduled') && (
        <div className="mt-4 sm:hidden">
          {weekDays
            .filter(d => d.isToday && d.assignments.length > 0)
            .map(day => {
              const assignment = day.assignments.find(
                a => a.status === 'pending' || a.status === 'reminded' || a.status === 'confirmed'
              );
              if (!assignment) return null;

              const workoutName = getAssignmentWorkoutName(assignment);

              return (
                <button
                  key={assignment.id}
                  onClick={() => onStartWorkout?.(assignment)}
                  className="
                    w-full flex items-center justify-between
                    p-3 rounded-lg
                    bg-primary-50 dark:bg-primary-500/10
                    border border-primary-200 dark:border-primary-500/30
                    hover:bg-primary-100 dark:hover:bg-primary-500/20
                    transition
                  "
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Today's Workout
                      </p>
                      <p className="text-xs text-gray-600 dark:text-slate-400">
                        {workoutName}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                    Start →
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
